export interface BookingSummary {
    id: string | number;
    reference?: string;
    title: string;
    venue?: string;
    date?: string;
    startTime?: string;
    endTime?: string;
    amount?: number;
    currencyCode?: string;
    status: string;
    customerName?: string;
    customerContact?: string;
    quantity?: number;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
    typeof value === 'string' ? value : undefined;

const readNumber = (value: unknown): number | undefined => {
    if (typeof value === 'number') return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
        return Number(value);
    }
    return undefined;
};

const pickFirstString = (...values: Array<unknown>): string | undefined => {
    for (const value of values) {
        const str = readString(value);
        if (str && str.trim()) return str;
    }
    return undefined;
};

export const toBookingSummary = (record: Record<string, unknown>): BookingSummary => {
    const appointmentable = isRecord(record.appointmentable) ? record.appointmentable : null;
    const pitch = isRecord(record.pitch) ? record.pitch : null;
    const user = isRecord(record.user) ? record.user : null;

    const title =
        pickFirstString(
            appointmentable?.title,
            appointmentable?.name,
            record.name,
            pitch?.name
        ) || 'N/A';

    const venue = pickFirstString(
        record.venue_name,
        record.venue,
        record.location,
        pitch?.name,
        appointmentable?.name
    );

    const date = pickFirstString(
        appointmentable?.date,
        record.date,
        record.booking_date
    );

    const startTime = pickFirstString(
        appointmentable?.start_time,
        record.start_time
    );

    const endTime = pickFirstString(
        appointmentable?.end_time,
        record.end_time
    );

    const amount =
        readNumber(record.amount) ??
        readNumber(record.total_amount) ??
        readNumber(record.price) ??
        readNumber(appointmentable?.price);

    const currencyCode = pickFirstString(
        appointmentable?.currency,
        appointmentable?.price_currency,
        record.currency,
        record.price_currency
    );

    const status = pickFirstString(record.status) || 'pending';

    const summary: BookingSummary = {
        id: (record.id as string | number) ?? record.reference ?? title,
        title,
        status,
    };

    const reference = pickFirstString(record.reference);
    if (reference) summary.reference = reference;

    if (venue) summary.venue = venue;
    if (date) summary.date = date;
    if (startTime) summary.startTime = startTime;
    if (endTime) summary.endTime = endTime;
    if (amount !== undefined) summary.amount = amount;
    if (currencyCode) summary.currencyCode = currencyCode;

    const customerName = pickFirstString(user?.name, record.booked_by);
    if (customerName) summary.customerName = customerName;

    const customerContact = pickFirstString(user?.email, user?.phone);
    if (customerContact) summary.customerContact = customerContact;

    const quantity = readNumber(record.quantity);
    if (quantity !== undefined) summary.quantity = quantity;

    return summary;
};

export const formatBookingDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;

    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
    const day = date.getDate();
    const daySuffix =
        day === 1 || day === 21 || day === 31
            ? 'st'
            : day === 2 || day === 22
                ? 'nd'
                : day === 3 || day === 23
                    ? 'rd'
                    : 'th';

    return date
        .toLocaleDateString('en-US', options)
        .replace(/\d+/, `${day}${daySuffix}`);
};

export const formatBookingTime = (time?: string): string => {
    if (!time || time === 'N/A') return 'N/A';
    const parts = time.length === 8 ? time.slice(0, 5).split(':') : time.split(':');
    if (parts.length < 2) return time;

    const hoursStr = parts[0] ?? '0';
    const minutes = parts[1] ?? '00';
    let hours = parseInt(hoursStr, 10);
    if (Number.isNaN(hours)) return time;
    const period = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    return `${hours}:${minutes} ${period}`;
};

export const formatBookingTimeRange = (start?: string, end?: string): string => {
    const startLabel = formatBookingTime(start);
    if (!end || end === 'N/A') return startLabel;
    return `${startLabel} - ${formatBookingTime(end)}`;
};
